import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import { constants } from "@reactivated/constants";

import { formatNumber } from "../../utils/format";
import { usePageSetting } from "../../utils/settings";

const AppSlug = constants["tsicommon.types.AppSlug"];

const STAR_FULL =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15L.8 21.1c-.4-.4-.8-.9-.8-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5";

const STAR_HALF =
    "m38.3 31.7 2.6 15c.2 1.3-.3 2.1-1.2 2.1-.3 0-.7-.1-1.2-.4l-13.5-7-13.5 7.1c-.5.2-.9.4-1.2.4-.9 0-1.4-.8-1.2-2.1l2.6-15L.8 21.1c-1.3-1.3-.9-2.6.9-2.8l15.1-2.2 6.8-13.7q.6-1.2 1.5-1.2c.5 0 1.1.4 1.5 1.2l6.8 13.7 15.1 2.2c1.8.3 2.2 1.5.9 2.8zm5.1-10.3-10.7-1.6-2-.3-.9-1.8L25 8.1V37l1.8.9 9.6 5.1-1.8-10.7-.4-2 1.5-1.4z";

const STAR_OUTLINE =
    "M49.2 21.1 38.3 31.7l2.6 15v.6c0 .8-.4 1.5-1.2 1.5-.4 0-.8-.2-1.2-.4l-13.5-7-13.5 7.1c-.4.2-.8.4-1.2.4-.9 0-1.3-.7-1.3-1.5 0-.2 0-.4.1-.6l2.6-15-11-10.7c-.3-.4-.7-.9-.7-1.5 0-.9.9-1.3 1.7-1.4L16.8 16l6.8-13.7c.3-.6.8-1.2 1.5-1.2s1.2.7 1.5 1.2L33.4 16l15.1 2.2c.7.1 1.7.5 1.7 1.4-.2.6-.6 1.1-1 1.5m-5.8.3-12.7-1.9L25 8.1l-5.7 11.5-12.7 1.8 9.2 8.9L13.6 43 25 37l11.3 6-2.2-12.6z";

const starPath = (rating: number, index: number): string => {
    if (rating >= index + 0.75) {
        return STAR_FULL;
    }
    if (rating >= index + 0.25) {
        return STAR_HALF;
    }
    return STAR_OUTLINE;
};

const StarSvg = (props: Immutable<{ d: string }>): React.JSX.Element => (
    <svg
        className="star-rating__star"
        role="img"
        aria-hidden="true"
        xmlns="http://www.w3.org/2000/svg"
        xmlSpace="preserve"
        width="50"
        height="50"
        viewBox="0 0 50 50"
    >
        <title>{t`Star Rating`}</title>
        <path d={props.d} />
    </svg>
);

/**
 * Star-rating graphic for tempurpedic product cards and the pillows-landing
 * page: renders five 50x50 star glyphs whose fill reflects ``rating`` (full at
 * ``>= index + 0.75``, half at ``>= index + 0.25``, outline otherwise) inside
 * ``star-rating`` / ``star-rating__inner-container`` / ``star-rating__stars``
 * markup, preceded by a screen-reader-only rating label. ``number`` adds the
 * ``star-rating__number`` review-count row (thousands-separated) only when
 * truthy; ``className`` extends the root ``star-rating`` element. Renders
 * nothing on the tempurpedic.ca (``tempurca``) app.
 *
 * The star glyphs are tempurpedic-specific -- not for cross-site reuse; other
 * sites have their own star components.
 */
export const SubBlockStarRating = (
    props: Immutable<{
        rating: number;
        number?: number | null;
        className?: string;
    }>,
): React.JSX.Element | null => {
    const appSlug = usePageSetting("app_slug") || "";
    if (appSlug === AppSlug.TEMPURCA) {
        return null;
    }
    const { rating, number, className } = props;
    return (
        <div className={`star-rating ${className ?? ""}`}>
            <div className="star-rating__inner-container">
                <div className="star-rating__stars">
                    <span className="ada-screenreader-only">
                        {t`Rated ${rating} out of 5 stars`}
                    </span>
                    {[0, 1, 2, 3, 4].map((index) => (
                        <StarSvg key={index} d={starPath(rating, index)} />
                    ))}
                </div>
                {number ? (
                    <div className="star-rating__number">
                        {formatNumber(number)} {t`Reviews`}
                    </div>
                ) : null}
            </div>
        </div>
    );
};
